/**
 * The deriver names, which are also the `derived_state.name` values, in the table's order.
 * `fields` comes after `clock` (deliverable 16b): a runner that walks `DERIVERS` in order (1.A's
 * write runner on the `meta`, `write`, `clock` and `calendar` triggers) runs it after the
 * chronology derivers whose spans, bounds and present tick it reads. Save orders are separate.
 * `inbox` (CS r5c deliverable 171) is appended last, so no deriver's `DERIVED_LOCK_SPACE` key moves.
 */
export const DERIVER_NAMES = [
    'body',
    'secrets',
    'names',
    'ticks',
    'spans',
    'solver',
    'clock',
    'fields',
    'play',
    'discovery',
    'sheets',
    'inbox',
];
/**
 * When a deriver runs: `save` in the body-save transaction (in `saveOrder`); `meta` in a metadata
 * write's transaction (title, aliases, canon status, props, trash, merge, retype); `write` on a
 * write to the rows it reads (links, placements, states, records, sheets); `field_def` inside the
 * `field_def` transaction; `clock` on a change of the campaign clock; `calendar` on a calendar
 * commit; `seed` when the SRD seed writes the derived rows of reference documents. A deriver may
 * keep `save` for creation only: `fields` derives a page's values when it is created and otherwise
 * from its other triggers (CS r5c deliverable 171, 1.A stage 4 deliverable 36's request), so its
 * `runOnSave` may skip a save that changes no input it reads.
 */
export const DERIVER_TRIGGERS = [
    'save',
    'meta',
    'write',
    'field_def',
    'clock',
    'calendar',
    'seed',
];
/** How a stored derived row differs from its recomputation (`DriftV1.kind`). */
export const DRIFT_KINDS = ['missing', 'extra', 'different'];
/**
 * The values of `derived_state.status`, in this order (1.0b deliverable 16b): `ok`; `stale`, which
 * 1.A writes when a deriver's runner fails, so the deriver is rebuilt; `drift`, which 1.P writes
 * when `db:verify` finds drift (1.P also writes `ok`). 1.V-1 shows them on the Health page. A
 * check on the column, if 1.0a or CS adds one, lists exactly these.
 */
export const DERIVED_STATE_STATUSES = ['ok', 'stale', 'drift'];
export const DERIVERS = [
    {
        name: 'body',
        owner: '1.A',
        // `asset_ref` rows with `via = 'body'` only (baseline section 3, derived registry).
        writes: [
            'document.body_text',
            'document.word_count',
            'block_index',
            'mention',
            'asset_ref[via=body]',
        ],
        // `deleted_at` is one of its inputs: trashing clears these rows and restoring rederives them.
        when: ['save', 'meta', 'seed'],
        saveOrder: 10,
    },
    {
        name: 'secrets',
        owner: '1.M-1',
        // The secret's content from the body, and its orphaning.
        writes: [
            'secret.document_id',
            'secret.block_id',
            'secret.label',
            'secret.text',
            'secret.trigger',
            'secret.orphaned_at',
        ],
        when: ['save'],
        saveOrder: 20,
    },
    {
        name: 'names',
        owner: '1.D',
        writes: ['doc_name', 'document.search'],
        when: ['save', 'meta', 'seed'],
        saveOrder: 30,
    },
    {
        name: 'ticks',
        owner: '1.L-1',
        writes: [
            'time_point.lo_tick',
            'time_point.hi_tick',
            'time_point.calendar_version',
            'time_point.tick_status',
            'time_point.review_reason',
        ],
        when: ['write', 'calendar'],
        saveOrder: null,
    },
    {
        name: 'spans',
        owner: '1.L-2',
        writes: [
            'link.certain_span',
            'link.possible_span',
            'link.span_status',
            'entity_state.certain_span',
            'entity_state.possible_span',
            'placement.span',
        ],
        when: ['write', 'calendar'],
        saveOrder: null,
    },
    {
        name: 'solver',
        owner: '1.L-2',
        // Effective status caps a placement by its document's canon status, so a metadata write runs it.
        writes: [
            'placement.bound_lo',
            'placement.bound_hi',
            'placement.chron_rank',
            'placement.effective_status',
            'chron_constraint.status',
            'chron_violation',
        ],
        when: ['meta', 'write', 'calendar'],
        saveOrder: null,
    },
    {
        name: 'clock',
        owner: '1.L-2',
        writes: [
            'campaign_clock.present_tick',
            'campaign_clock.present_parts',
            'clock_advance.from_tick',
            'clock_advance.to_tick',
        ],
        when: ['clock', 'calendar'],
        saveOrder: null,
    },
    {
        name: 'fields',
        owner: '1.B-1',
        // Every source of `field_value`: props, links, placements (`world_date`), states (with the
        // `as_of_now` rows) and, for reference documents, `rules`; and the `asset_ref` rows of asset
        // fields. After `clock`, whose present tick it reads, as it reads spans and bounds.
        writes: ['field_value', 'asset_ref[via=prop]'],
        when: ['save', 'meta', 'write', 'field_def', 'clock', 'calendar', 'seed'],
        saveOrder: 40,
    },
    {
        name: 'play',
        owner: '1.M-1',
        // A retype can add or remove `playable`, which makes the state `n_a`.
        writes: [
            'document.play_state',
            'document.play_note',
            'document.play_progress',
            'play_record.fold_session_no',
            // CS r5a (deliverable 149): a run-sheet item takes its latest record (1.M-1 stage s3).
            'session_item.play_state',
            'session_item.outcome_note',
        ],
        when: ['meta', 'write'],
        saveOrder: null,
    },
    {
        name: 'discovery',
        owner: '1.M-1',
        writes: [
            'document.discovery',
            'document.discovered_session_id',
            'document.derived_rev',
            'secret.state',
            'secret.first_known_session_id',
            // From the knowledge events, as migration 0010 says (CS r2b; 1.M-1 open point 2).
            'secret.known_before_tracking',
            'knowledge_event.fold_session_no',
            'knowledge_event.fold_tick',
        ],
        when: ['write', 'calendar'],
        saveOrder: null,
    },
    {
        // Passed from 1.AA to 1.AC-1 with the sheet writes (A2.1 section 1, "Derived registry"; CS
        // item AC1), then to 2.I at its stage s2a (DECISIONS 222 ruling 3, CS consenting), whose
        // deriver dispatches on the sheet's kind.
        name: 'sheets',
        owner: '2.I',
        writes: ['actor_sheet.derived'],
        when: ['write', 'seed'],
        saveOrder: null,
    },
    {
        // CS r5c deliverable 171 (DECISIONS 548, 607): the inbox's stored unlinked hits and page facts
        // (1.N-3 s5), after `names`, whose `doc_name` rows it reads; a metadata write (aliases, trash,
        // merge, canon status) runs it too. A stub until 1.N-3 s5 fills it.
        name: 'inbox',
        owner: '1.N-3',
        writes: ['unlinked_hit', 'inbox_fact'],
        when: ['save', 'meta'],
        saveOrder: 50,
    },
];
//# sourceMappingURL=rows-derivers.js.map